import { Puzzle, LeaderboardResponse, LeaderboardView } from "../types";

const API_BASE = import.meta.env.VITE_API_URL || "/api";
const TIMEOUT_MS = 10_000;
declare const __WORD_LIST_VERSION__: string;
const WLV = typeof __WORD_LIST_VERSION__ !== "undefined" ? __WORD_LIST_VERSION__ : "1";

function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, { credentials: "include", signal: AbortSignal.timeout(TIMEOUT_MS), ...init });
}

// --- Auth ---

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  leaderboardName: string | null;
}

export async function verifyGoogleCredential(credential: string): Promise<AuthUser> {
  const res = await fetchWithTimeout(`${API_BASE}/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) throw new Error("Authentication failed");
  return res.json();
}

export async function fetchMe(): Promise<AuthUser | null> {
  try {
    const res = await fetchWithTimeout(`${API_BASE}/auth/me`);
    if (res.status === 401) return null;
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  await fetchWithTimeout(`${API_BASE}/auth/logout`, { method: "POST" });
}

// --- Progress Sync ---

export async function fetchProgress(): Promise<Record<string, unknown> | null> {
  try {
    const res = await fetchWithTimeout(`${API_BASE}/progress`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function syncProgress(progress: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  try {
    const res = await fetchWithTimeout(`${API_BASE}/progress/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(progress),
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function fetchPuzzle(
  size: string,
  date: string
): Promise<Puzzle> {
  const res = await fetchWithTimeout(`${API_BASE}/puzzle/${size}/${date}?v=${WLV}`);
  if (!res.ok) throw new Error("Failed to fetch puzzle");
  return res.json();
}

export async function fetchRandomPuzzle(
  size: "7x7" | "9x9",
  difficulty?: string
): Promise<Puzzle> {
  const params = difficulty ? `?difficulty=${difficulty}` : "";
  const res = await fetchWithTimeout(`${API_BASE}/puzzle/random/${size}${params}`);
  if (!res.ok) throw new Error("Failed to generate random puzzle");
  return res.json();
}

// --- Leaderboard ---

export async function fetchLeaderboard(
  size: string,
  view: LeaderboardView,
  date?: string,
  fresh?: boolean,
): Promise<LeaderboardResponse> {
  const sp = new URLSearchParams();
  if (date) sp.set("date", date);
  if (fresh) sp.set("fresh", "1");
  const params = sp.size > 0 ? `?${sp}` : "";
  const res = await fetchWithTimeout(`${API_BASE}/leaderboard/${size}/${view}${params}`);
  if (!res.ok) throw new Error("Failed to fetch leaderboard");
  return res.json();
}

export async function leaderboardOptOut(): Promise<void> {
  await fetchWithTimeout(`${API_BASE}/leaderboard/opt-out`, { method: "POST" });
}

export async function leaderboardOptIn(): Promise<{ leaderboardName: string }> {
  const res = await fetchWithTimeout(`${API_BASE}/leaderboard/opt-in`, { method: "POST" });
  if (!res.ok) throw new Error("Failed to opt in");
  return res.json();
}
